import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Layers, Plus, Edit2, Trash2, X, Ruler, Palette, Building2, FileDown } from "lucide-react";

const todayIST = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
};
const monthIST = () => todayIST().slice(0, 7);

export default function DtfSheets() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_meters: 0, total_designs: 0, count: 0 });
  const [vendors, setVendors] = useState(["Gocrazy", "Loaffer"]);
  const [monthFilter, setMonthFilter] = useState(monthIST());
  const [vendorFilter, setVendorFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    try {
      const params = {};
      if (monthFilter) params.month = monthFilter;
      if (vendorFilter) params.vendor = vendorFilter;
      const { data } = await api.get("/dtf-sheets", { params });
      setRows(data.items || []);
      setTotals({ total_meters: data.total_meters || 0, total_designs: data.total_designs || 0, count: data.count || 0 });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  async function loadVendors() {
    try {
      const { data } = await api.get("/dtf-sheets/vendors");
      setVendors(data.items || []);
    } catch { /* noop */ }
  }
  useEffect(() => { load(); loadVendors(); }, [monthFilter, vendorFilter]); // eslint-disable-line

  async function onDelete(id) {
    if (!window.confirm("Delete this DTF sheet row?")) return;
    try {
      await api.delete(`/dtf-sheets/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  function exportExcel() {
    const params = new URLSearchParams();
    if (monthFilter) params.set("month", monthFilter);
    if (vendorFilter) params.set("vendor", vendorFilter);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/dtf-sheets/export.xlsx?${params.toString()}`;
    // Session cookie authenticates the download
    window.open(url, "_blank");
    toast.success("Preparing Excel — download will start shortly");
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl">
      <div className="brut-badge mb-4">DTF SHEETS · PRINT ROOM</div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter leading-none mb-2">
            DTF Roll<br />& Print Log.
          </h1>
          <p className="text-zinc-600 max-w-xl text-sm sm:text-base">
            Track every metre of DTF roll consumed and every design you send out — Gocrazy, Loaffer, or any other vendor.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={exportExcel}
            className="brut-btn-outline px-4 py-2.5 inline-flex items-center gap-2"
            data-testid="dtf-export-btn"
          >
            <FileDown size={16} /> Download Excel
          </button>
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="brut-btn px-5 py-2.5 inline-flex items-center gap-2"
            data-testid="dtf-add-btn"
          >
            <Plus size={16} /> Log New Row
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Tile icon={Ruler} label="Total roll consumed" value={`${totals.total_meters} m`} testid="dtf-total-meters" />
        <Tile icon={Palette} label="Designs submitted" value={totals.total_designs} testid="dtf-total-designs" />
        <Tile icon={Layers} label="Rows this month" value={totals.count} testid="dtf-count" />
        <Tile icon={Building2} label="Unique vendors" value={vendors.length} testid="dtf-vendors-count" />
      </div>

      {/* Filters */}
      <div className="brut-card-flat p-4 mb-6 flex flex-wrap items-end gap-3">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Month</div>
          <input
            type="month"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="brut-input"
            data-testid="dtf-month-filter"
          />
        </div>
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Vendor</div>
          <select
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            className="brut-input min-w-[160px]"
            data-testid="dtf-vendor-filter"
          >
            <option value="">All vendors</option>
            {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        {vendorFilter && (
          <button onClick={() => setVendorFilter("")} className="brut-btn-outline px-3 py-1.5 text-xs inline-flex items-center gap-1">
            <X size={12} /> Clear vendor
          </button>
        )}
      </div>

      {/* Table */}
      <div className="brut-card-flat p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3E9DA]">
            <tr>
              <Th>Date</Th>
              <Th>Vendor</Th>
              <Th className="text-right">Roll (m)</Th>
              <Th className="text-right">Designs</Th>
              <Th>Notes</Th>
              <Th>Logged by</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-zinc-500">No rows for this filter. <button onClick={() => setShowForm(true)} className="underline font-bold">Log the first one →</button></td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t-2 border-[#2F343A]/10 hover:bg-[#FDF6EE]/60" data-testid={`dtf-row-${r.id}`}>
                <Td className="font-mono-ds text-xs">{r.date}</Td>
                <Td>
                  <span className="brut-chip">{r.vendor}</span>
                </Td>
                <Td className="text-right font-mono-ds font-bold">{r.roll_meters} m</Td>
                <Td className="text-right font-mono-ds font-bold">{r.designs_submitted}</Td>
                <Td className="text-zinc-600 text-xs max-w-[280px] truncate">{r.notes || "—"}</Td>
                <Td className="text-xs text-zinc-500">{r.created_by_name || "—"}</Td>
                <Td className="text-right whitespace-nowrap">
                  {isAdmin && (
                    <>
                      <button onClick={() => { setEditing(r); setShowForm(true); }} className="p-1.5 border-2 border-black hover:bg-[#F3E9DA]" data-testid={`dtf-edit-${r.id}`} aria-label="Edit">
                        <Edit2 size={12} />
                      </button>
                      <button onClick={() => onDelete(r.id)} className="p-1.5 border-2 border-black hover:bg-[#FDECE0] ml-1.5" data-testid={`dtf-delete-${r.id}`} aria-label="Delete">
                        <Trash2 size={12} className="text-[#B34A00]" />
                      </button>
                    </>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <DtfForm
          initial={editing}
          vendors={vendors}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); loadVendors(); }}
        />
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, testid }) {
  return (
    <div className="brut-card-flat p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-[#7A3E1F]" />
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">{label}</div>
      </div>
      <div className="font-display font-black text-2xl sm:text-3xl">{value}</div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-left font-mono-ds text-[10px] tracking-widest uppercase text-zinc-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 ${className}`}>{children}</td>;
}

function DtfForm({ initial, vendors, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: initial?.date || todayIST(),
    roll_meters: initial?.roll_meters ?? "",
    designs_submitted: initial?.designs_submitted ?? "",
    vendor: initial?.vendor || "",
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;

  async function submit(e) {
    e.preventDefault();
    if (!form.vendor.trim()) { toast.error("Vendor is required"); return; }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        roll_meters: Number(form.roll_meters || 0),
        designs_submitted: Number(form.designs_submitted || 0),
        vendor: form.vendor.trim(),
        notes: form.notes || "",
      };
      if (editing) await api.put(`/dtf-sheets/${initial.id}`, payload);
      else await api.post("/dtf-sheets", payload);
      toast.success(editing ? "Row updated" : "Row logged");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="brut-card bg-[#FDF6EE] w-full max-w-lg p-5 space-y-4"
        data-testid="dtf-form"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="brut-badge">{editing ? "EDIT DTF ROW" : "NEW DTF ROW"}</div>
            <h2 className="font-display font-black text-2xl mt-2 leading-tight">
              {editing ? "Update log entry" : "Log a DTF sheet"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 border-2 border-black bg-white" aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Date">
            <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="brut-input w-full" data-testid="dtf-date-input" />
          </Field>
          <Field label="Vendor">
            <input
              list="dtf-vendor-list"
              required
              placeholder="Gocrazy, Loaffer or type new…"
              value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              className="brut-input w-full"
              data-testid="dtf-vendor-input"
            />
            <datalist id="dtf-vendor-list">
              {vendors.map((v) => <option key={v} value={v} />)}
            </datalist>
          </Field>
          <Field label="Roll consumed (metres)">
            <input type="number" min="0" step="0.01" required value={form.roll_meters} onChange={(e) => setForm({ ...form, roll_meters: e.target.value })} className="brut-input w-full" data-testid="dtf-meters-input" />
          </Field>
          <Field label="Designs submitted (count)">
            <input type="number" min="0" step="1" required value={form.designs_submitted} onChange={(e) => setForm({ ...form, designs_submitted: e.target.value })} className="brut-input w-full" data-testid="dtf-designs-input" />
          </Field>
        </div>
        <Field label="Notes (optional)">
          <textarea rows={2} placeholder="Roll batch #, design list, remarks…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="brut-input w-full" data-testid="dtf-notes-input" />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="brut-btn-outline px-4 py-2">Cancel</button>
          <button type="submit" disabled={saving} className="brut-btn px-5 py-2 inline-flex items-center gap-2" data-testid="dtf-save-btn">
            <Plus size={14} /> {saving ? "Saving…" : editing ? "Update" : "Log row"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-600 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
